import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, Package } from "lucide-react";
import CandleLogChart from "@/pages/CandleLogChart";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function downloadFile(url, filename) {
  // Auth via the httpOnly session cookie — `credentials: "include"` sends
  // it cross-origin. `mode: "cors"` is implicit for cross-origin URLs.
  fetch(url, { credentials: "include" })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    })
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    })
    .catch((e) => toast.error(`Download failed: ${e.message}`));
}

export default function CandleLogs() {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState("");
  const [underlying, setUnderlying] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seriesStats, setSeriesStats] = useState(null);
  // Underlyings enabled for paper trading — driven by the strategy universe
  // so SENSEX (and any future addition) shows up automatically.
  const [availableUnderlyings, setAvailableUnderlyings] = useState(["NIFTY", "BANKNIFTY"]);

  // Sorted rejection codes (desc by count) — memoized to avoid sort on
  // every keystroke/re-render.
  const sortedRejectionCodes = useMemo(
    () => Object.entries(summary?.rejection_codes || {}).sort((a, b) => b[1] - a[1]),
    [summary],
  );

  const loadDates = async () => {
    try {
      const { data } = await api.get("/candle-logs/dates");
      setDates(data.dates || []);
      if (!date && data.dates?.length) setDate(data.dates[0]);
    } catch (e) {
      toast.error("Failed to load dates");
    }
  };

  const loadSeriesStats = async (d) => {
    if (!d) return;
    try {
      const { data } = await api.get("/candle-logs/option-series", { params: { date: d } });
      setSeriesStats({
        count: data.count || 0,
        symbols: (data.symbols || []).length,
      });
    } catch (e) {
      setSeriesStats({ count: 0, symbols: 0 });
    }
  };

  const loadRows = async (d, u) => {
    if (!d) return;
    setLoading(true);
    try {
      const params = { date: d };
      if (u) params.underlying = u;
      const [rowsRes, sumRes] = await Promise.all([
        api.get("/candle-logs", { params }),
        api.get("/candle-logs/summary", { params: { date: d } }),
      ]);
      setRows(rowsRes.data.rows || []);
      setSummary(sumRes.data || null);
    } catch (e) {
      toast.error("Failed to load candle logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDates();
    // Fetch the strategy universe so the underlying dropdown reflects
    // exactly what the engine is evaluating (NIFTY/BANKNIFTY/SENSEX/…).
    api.get("/strategy/universe")
      .then(({ data }) => {
        const list = data?.active_paper || data?.active_live || [];
        if (list.length) setAvailableUnderlyings(list);
      })
      .catch(() => { /* keep hardcoded fallback */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (date) {
      loadRows(date, underlying);
      loadSeriesStats(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, underlying]);

  return (
    <div className="space-y-4" data-testid="candle-logs-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Candle Logs</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
            Every 5-min bar + indicators + option snapshot + evaluation result · 30-day rolling
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reload-btn"
            onClick={() => { loadDates(); loadRows(date, underlying); loadSeriesStats(date); }}
            className="px-3 py-2 border border-slate-700 hover:bg-slate-800/50 rounded-sm text-xs uppercase tracking-[0.15em] text-slate-300 flex items-center gap-2"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            data-testid="download-all-btn"
            onClick={() =>
              downloadFile(`${API_BASE}/api/candle-logs/export-all`, "myedge-candles-all.zip")
            }
            className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm flex items-center gap-2"
          >
            <Package size={12} /> Download All (ZIP)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="candle-stats-tiles">
        <div className="surface rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Candle Bars
          </div>
          <div className="text-2xl font-mono text-slate-100 mt-1" data-testid="tile-bars-count">
            {rows.length}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {date || "—"} · {underlying || "both"}
          </div>
        </div>
        <div className="surface rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Accepted
          </div>
          <div className="text-2xl font-mono text-emerald-400 mt-1" data-testid="tile-accepted-count">
            {summary?.accepted ?? 0}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {summary ? `${summary.rejected} rejected` : "—"}
          </div>
        </div>
        <div className="surface rounded-sm p-3" data-testid="tile-option-series">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Option-Series Ticks Today
          </div>
          <div className="text-2xl font-mono text-blue-300 mt-1" data-testid="tile-option-series-count">
            {seriesStats?.count?.toLocaleString?.() ?? "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {seriesStats ? `${seriesStats.symbols} option symbol${seriesStats.symbols === 1 ? "" : "s"} tracked` : "—"}
          </div>
        </div>
        <div className="surface rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Trading Dates Available
          </div>
          <div className="text-2xl font-mono text-slate-100 mt-1" data-testid="tile-dates-count">
            {dates.length}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">30-day rolling</div>
        </div>
      </div>

      <div className="surface rounded-sm p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 block">
            Trading Date
          </label>
          <select
            data-testid="date-select"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm font-mono min-w-[160px] focus:border-blue-500 outline-none"
          >
            {dates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 block">
            Underlying
          </label>
          <select
            data-testid="underlying-select"
            value={underlying}
            onChange={(e) => setUnderlying(e.target.value)}
            className="mt-1 bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm font-mono min-w-[140px] focus:border-blue-500 outline-none"
          >
            <option value="">All</option>
            {availableUnderlyings.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <button
          data-testid="download-day-btn"
          disabled={!date}
          onClick={() =>
            downloadFile(
              `${API_BASE}/api/candle-logs/export?date=${date}${underlying ? `&underlying=${underlying}` : ""}`,
              `myedge-candles-${date}${underlying ? `-${underlying}` : ""}.csv`,
            )
          }
          className="px-4 py-2 border border-blue-800 text-blue-300 hover:bg-blue-950 text-xs uppercase tracking-[0.15em] rounded-sm flex items-center gap-2 disabled:opacity-40"
        >
          <Download size={12} /> Download CSV for {date || "…"}
        </button>
        <div className="ml-auto text-xs text-slate-500 font-mono" data-testid="row-count">
          {loading ? "Loading…" : `${rows.length} rows`}
        </div>
      </div>

      <CandleLogChart rows={rows} />

      {summary && summary.total_bars > 0 && (
        <div className="surface rounded-sm p-4" data-testid="rejection-summary">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
              Rejection Breakdown · {summary.date}
            </div>
            <div className="text-[11px] font-mono text-slate-400">
              {summary.total_bars} bars ·{" "}
              <span className="text-emerald-400">{summary.accepted} accepted</span> ·{" "}
              <span className="text-slate-400">{summary.rejected} rejected</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-[11px]">
            {Object.entries(summary.per_condition).map(([k, v]) => {
              const total = v.passed + v.failed + v.unknown;
              const passPct = total > 0 ? (v.passed / total) * 100 : 0;
              const failedMost = v.failed > v.passed;
              return (
                <div
                  key={k}
                  className="border border-slate-800 rounded-sm p-2 flex flex-col"
                  data-testid={`cond-${k}`}
                >
                  <div className="text-slate-400 font-mono truncate">{k}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className={`text-lg font-mono ${failedMost ? "text-amber-400" : "text-emerald-400"}`}>
                      {v.passed}
                    </span>
                    <span className="text-slate-500 text-[10px]">/ {total}</span>
                    <span className="ml-auto text-[10px] text-slate-500">
                      {passPct.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-1 bg-slate-800 rounded-sm mt-1 overflow-hidden">
                    <div
                      className={`h-full ${failedMost ? "bg-amber-500/60" : "bg-emerald-500/70"}`}
                      style={{ width: `${passPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {sortedRejectionCodes.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-800 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono">
              <span className="text-slate-500 uppercase tracking-[0.15em]">By code:</span>
              {sortedRejectionCodes.map(([code, count]) => (
                <span key={code} className="text-slate-400">
                  <span className="text-blue-300">{code}</span>: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="surface rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[11px] font-mono">
            <thead className="bg-slate-900/60 text-slate-400 uppercase text-[9px] tracking-[0.15em]">
              <tr>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">U/L</th>
                <th className="text-right px-3 py-2">Close</th>
                <th className="text-right px-3 py-2">ADX</th>
                <th className="text-right px-3 py-2">ATR</th>
                <th className="text-right px-3 py-2">Vol Ratio</th>
                <th className="text-right px-3 py-2">EMA20/50</th>
                <th className="text-right px-3 py-2">VWAP</th>
                <th className="text-right px-3 py-2">Score</th>
                <th className="text-left px-3 py-2">Result</th>
                <th className="text-left px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody data-testid="candle-log-tbody">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-slate-500">
                    {loading ? "…" : "No data for this date."}
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const ind = r.indicators || {};
                const ev = r.evaluation || {};
                const t = String(r.candle_timestamp || "").slice(11, 16);
                const accepted = ev.accepted;
                return (
                  <tr
                    key={`${r.candle_timestamp}-${r.underlying}`}
                    className="border-t border-slate-800/60 hover:bg-slate-800/20"
                  >
                    <td className="px-3 py-1.5 text-slate-300">{t}</td>
                    <td className="px-3 py-1.5 text-slate-300">{r.underlying}</td>
                    <td className="px-3 py-1.5 text-right text-slate-100">
                      {ind.close != null ? Number(ind.close).toFixed(2) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right">{ind.adx14?.toFixed(1) ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">{ind.atr14?.toFixed(1) ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">{ind.volume_ratio?.toFixed(2) ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {ind.ema20 != null && ind.ema50 != null
                        ? `${ind.ema20.toFixed(1)} / ${ind.ema50.toFixed(1)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {ind.session_vwap?.toFixed(1) ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {ev.score != null ? Number(ev.score).toFixed(3) : "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      {accepted ? (
                        <span className="text-emerald-400">ACCEPTED</span>
                      ) : (
                        <span className="text-slate-500">rejected</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-slate-400 truncate max-w-[240px]">
                      {ev.rejection_code || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
